import type { ComponentProps, ReactElement, ReactNode } from "react"
import type { LucideIcon } from "lucide-react"
import { MoreHorizontalIcon } from "lucide-react"
import { v4 as uuidv4 } from "uuid"

import {
  DropdownMenu as DropdownMenuRoot,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu"
import { cn } from "@/shadcn/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import { Icon } from "@/shared/components/Icon"
import { withStoppedClickPropagation, withStoppedPointerPropagation } from "@/shared/lib/utils"

type DropdownMenuActionOption = {
  type: "action"
  label: ReactNode
  icon?: LucideIcon
  shortcut?: ReactNode
  active?: boolean
  disabled?: boolean
  inset?: boolean
  className?: string
  variant?: "default" | "destructive"
  onSelect?: ComponentProps<typeof DropdownMenuItem>["onSelect"]
}

type DropdownMenuSubmenuOption = {
  type: "submenu"
  label: ReactNode
  items: DropdownMenuOption[]
  icon?: LucideIcon
  disabled?: boolean
  inset?: boolean
  className?: string
  contentProps?: Omit<ComponentProps<typeof DropdownMenuSubContent>, "children">
}

type DropdownMenuGroupOption = {
  type: "group"
  items: DropdownMenuOption[]
  label?: ReactNode
  insetLabel?: boolean
}

type DropdownMenuLabelOption = {
  type: "label"
  label: ReactNode
  inset?: boolean
  className?: string
}

type DropdownMenuSeparatorOption = {
  type: "separator"
  className?: string
}

export type DropdownMenuOption =
  | DropdownMenuActionOption
  | DropdownMenuSubmenuOption
  | DropdownMenuGroupOption
  | DropdownMenuLabelOption
  | DropdownMenuSeparatorOption

export type DropdownMenuOptions = {
  items: DropdownMenuOption[]
  contentProps?: Omit<ComponentProps<typeof DropdownMenuContent>, "children">
}

type DropdownMenuProps = Omit<ComponentProps<typeof DropdownMenuRoot>, "children"> & {
  options: DropdownMenuOptions
  renderTrigger?: () => ReactElement
  triggerProps?: Omit<ComponentProps<typeof DropdownMenuTrigger>, "asChild" | "children">
  defaultTrigger?: LucideIcon
  defaultTriggerProps?: Omit<ComponentProps<typeof Button>, "children">
  matchTriggerWidth?: boolean
}

const renderMenuOption = (item: DropdownMenuOption): ReactNode => {
  const itemId = uuidv4()

  switch (item.type) {
    case "group": {
      return (
        <DropdownMenuGroup key={itemId}>
          {item.label && <DropdownMenuLabel inset={item.insetLabel}>{item.label}</DropdownMenuLabel>}
          {item.items.map(renderMenuOption)}
        </DropdownMenuGroup>
      )
    }

    case "label": {
      return (
        <DropdownMenuLabel key={itemId} inset={item.inset} className={item.className}>
          {item.label}
        </DropdownMenuLabel>
      )
    }

    case "separator": {
      return <DropdownMenuSeparator key={itemId} className={item.className} />
    }

    case "submenu": {
      const Icon = item.icon
      const { onClick, onPointerDown, ...contentProps } = item.contentProps ?? {}

      return (
        <DropdownMenuSub key={itemId}>
          <DropdownMenuSubTrigger disabled={item.disabled} inset={item.inset} className={item.className}>
            {Icon && <Icon />}
            {item.label}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              {...contentProps}
              onClick={withStoppedClickPropagation(onClick)}
              onPointerDown={withStoppedPointerPropagation(onPointerDown)}
            >
              {item.items.map(renderMenuOption)}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      )
    }

    case "action": {
      const Icon = item.icon

      return (
        <DropdownMenuItem
          key={itemId}
          active={item.active}
          disabled={item.disabled}
          inset={item.inset}
          className={cn("cursor-pointer", item.className)}
          variant={item.variant}
          onSelect={item.onSelect}
        >
          {Icon && <Icon />}
          {item.label}
          {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
        </DropdownMenuItem>
      )
    }
  }
}

export default function DropdownMenu({
  options,
  renderTrigger,
  triggerProps,
  defaultTrigger = MoreHorizontalIcon,
  defaultTriggerProps,
  matchTriggerWidth,
  ...rootProps
}: DropdownMenuProps) {
  const {
    onClick: onContentClick,
    onPointerDown: onContentPointerDown,
    onCloseAutoFocus,
    ...contentProps
  } = options.contentProps ?? {}
  const { onClick: onTriggerClick, onPointerDown: onTriggerPointerDown, ...restTriggerProps } = triggerProps ?? {}
  const TriggerIcon = defaultTrigger
  const { className: defaultTriggerClassName, ...defaultTriggerButtonProps } = defaultTriggerProps ?? {}

  const handleCloseAutoFocus: ComponentProps<typeof DropdownMenuContent>["onCloseAutoFocus"] = (event) => {
    onCloseAutoFocus?.(event)

    if (event.defaultPrevented) {
      return
    }

    event.preventDefault()

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  return (
    <DropdownMenuRoot {...rootProps}>
      <DropdownMenuTrigger
        asChild
        {...restTriggerProps}
        onClick={withStoppedClickPropagation(onTriggerClick)}
        onPointerDown={withStoppedPointerPropagation(onTriggerPointerDown)}
      >
        {renderTrigger ? (
          renderTrigger()
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Open menu"
            {...defaultTriggerButtonProps}
            className={cn("rounded-full", defaultTriggerClassName)}
          >
            <Icon icon={TriggerIcon} size="sm" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        {...contentProps}
        matchTriggerWidth={matchTriggerWidth}
        onClick={withStoppedClickPropagation(onContentClick)}
        onCloseAutoFocus={handleCloseAutoFocus}
        onPointerDown={withStoppedPointerPropagation(onContentPointerDown)}
      >
        {options.items.map(renderMenuOption)}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}
